import { ReactNode, useMemo, useRef, useState } from 'react'
import { IntlShape, useIntl } from 'react-intl'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { DataTable, DataTableSortEvent } from 'primereact/datatable'
import { InputText } from 'primereact/inputtext'
import { ProgressSpinner } from 'primereact/progressspinner'
import { SelectButton } from 'primereact/selectbutton'
import { Tag } from 'primereact/tag'
import InfoTip from '../../../components/novarium/InfoTip'
import {
  OperatingRoomGroupBy,
  OperatingRoomGroupRowDTO,
  OperatingRoomGroupsDTO,
} from '../../../dto/operatingRoomIndicators/OperatingRoomIndicatorsDTO'
import { weekdayNames } from '../../ServicesStatistics/chartText'
import { SectionState } from '../../UrgencySummary/useSectionData'
import {
  formatCount,
  formatDuration,
  formatHoursPlain,
  formatPercent,
  NO_VALUE,
} from '../operatingRoomFormat'

/** Las agrupaciones en el orden en que se ofrecen. Es el mismo dato visto de otra manera (SelectButton, no pestañas). */
const GROUP_BY_OPTIONS: readonly { value: OperatingRoomGroupBy; messageId: string }[] = [
  { value: 'TYPE', messageId: 'OperatingRoomIndicatorsGroupByType' },
  { value: 'CODE', messageId: 'OperatingRoomIndicatorsGroupByCode' },
  { value: 'SURGEON', messageId: 'OperatingRoomIndicatorsGroupBySurgeon' },
  { value: 'ANESTHESIA', messageId: 'OperatingRoomIndicatorsGroupByAnesthesia' },
  { value: 'FINANCIER', messageId: 'OperatingRoomIndicatorsGroupByFinancier' },
  { value: 'AGENDA', messageId: 'OperatingRoomIndicatorsGroupByAgenda' },
]

/** Cómo se llama la primera columna según la agrupación. */
const NAME_HEADER_ID: Record<OperatingRoomGroupBy, string> = {
  TYPE: 'OperatingRoomIndicatorsColType',
  CODE: 'OperatingRoomIndicatorsColCode',
  SURGEON: 'OperatingRoomIndicatorsColSurgeon',
  ANESTHESIA: 'OperatingRoomIndicatorsColAnesthesia',
  FINANCIER: 'OperatingRoomIndicatorsColFinancier',
  AGENDA: 'OperatingRoomIndicatorsColAgenda',
}

/** Cuántas filas se ven antes de «Ver todos (N)». */
export const TOP_ROWS = 10

type SortField =
  | 'name' | 'surgeries' | 'sharePct' | 'urgent' | 'scheduledPct' | 'hoursUsed' | 'medianDurationMinutes'
  | 'roomName' | 'capacityHours' | 'insideBlockHours' | 'occupancyPct'

interface SortState {
  field: SortField
  order: 1 | -1
}

/** Orden inicial de cada agrupamiento: lo que más cirugías tiene, primero (como llegan del backend). */
const DEFAULT_SORT: SortState = { field: 'surgeries', order: -1 }

/** Una fila con su clave única (`dataKey` de la tabla): el id puede ser nulo (sin dato, o sin agenda) y su nombre ya está resuelto. */
type GroupRow = OperatingRoomGroupRowDTO & { rowKey: string; label: string }

function compareRows(a: GroupRow, b: GroupRow, sort: SortState): number {
  if (sort.field === 'name') return sort.order * a.label.localeCompare(b.label, 'es')
  if (sort.field === 'roomName') return sort.order * (a.roomName ?? '').localeCompare(b.roomName ?? '', 'es')
  const left = a[sort.field]
  const right = b[sort.field]
  // Sin dato, siempre al final
  if (left === null && right === null) return 0
  if (left === null) return 1
  if (right === null) return -1
  return sort.order * (left - right)
}

/** «40 · 33,3 %»: la cantidad y su parte del total de la fila. */
const countWithShare = (intl: IntlShape, count: number, pct: number | null): string =>
  `${formatCount(intl, count)} · ${formatPercent(intl, pct)}`

interface OperatingRoomGroupedTableProps {
  groupBy: OperatingRoomGroupBy
  onGroupByChange: (groupBy: OperatingRoomGroupBy) => void
  /** Sin la acción 46 no se ofrece «Cirujano». */
  canGroupBySurgeon: boolean
  /** Las filas de la agrupación elegida (consulta aparte, recién al abrir la pestaña o cambiar de agrupación). */
  groups: SectionState<OperatingRoomGroupsDTO>
  /** Una sola acción por fila: ver sus cirugías en la lista (filtra la pantalla por esa fila). */
  onDrill: (groupBy: OperatingRoomGroupBy, row: OperatingRoomGroupRowDTO) => void
}

/**
 * Las cirugías realizadas del período agrupadas por tipo de cirugía, código, cirujano (con la acción 46), anestesia,
 * financiador o agenda, en una sola tabla. Está completa en memoria: el buscador por nombre (con más de 10 filas) y el orden por
 * columna se resuelven acá. Se ven las 10 primeras filas y «Ver todos (N)» muestra el resto. El pie suma los totales del
 * backend. Cada fila tiene una sola acción, al final: ver sus cirugías en la lista. En «Agenda» cada fila es un bloque de
 * plantilla (nombre, quirófano y horario) con su capacidad, lo usado dentro de su bloque y su ocupación; «Sin agenda» son las
 * cirugías fuera de agenda y no tiene capacidad ni ocupación («—»).
 */
export default function OperatingRoomGroupedTable({
  groupBy, onGroupByChange, canGroupBySurgeon, groups, onDrill,
}: OperatingRoomGroupedTableProps) {
  const intl = useIntl()
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT)
  const [showAll, setShowAll] = useState(false)
  const days = useMemo(() => weekdayNames(intl), [intl])
  const isAgenda = groupBy === 'AGENDA'

  const groupOptions = GROUP_BY_OPTIONS
    .filter((option) => option.value !== 'SURGEON' || canGroupBySurgeon)
    .map((option) => ({ value: option.value, label: intl.formatMessage({ id: option.messageId }) }))

  function changeGroupBy(next: OperatingRoomGroupBy) {
    setSearch('')
    setSort(DEFAULT_SORT)
    setShowAll(false)
    onGroupByChange(next)
  }

  // Mientras llega otra consulta se sigue viendo lo anterior (atenuado), pero solo si es de la misma agrupación: las filas de
  // otra (con otras columnas y otros nombres) no se mezclan con la cabecera nueva
  const lastLoaded = useRef<{ groupBy: OperatingRoomGroupBy; data: OperatingRoomGroupsDTO } | null>(null)
  if (groups.data) lastLoaded.current = { groupBy, data: groups.data }
  const loaded = groups.data ?? (lastLoaded.current?.groupBy === groupBy ? lastLoaded.current.data : null)
  const loadedRows = loaded?.rows ?? null
  const totals = loaded?.total ?? null

  /** El nombre de la fila: «código · descripción» en CODE; «Sin agenda» o «Sin dato» si no tiene id. */
  const labelOf = (row: OperatingRoomGroupRowDTO): string => {
    if (row.id === null) return intl.formatMessage({ id: isAgenda ? 'OperatingRoomIndicatorsNoAgenda' : 'OperatingRoomIndicatorsNoData' })
    if (groupBy === 'CODE') return [row.code, row.name].filter(Boolean).join(' · ')
    return row.name ?? NO_VALUE
  }

  const allRows = useMemo<GroupRow[]>(
    () => (loadedRows ?? []).map((row) => ({ ...row, label: labelOf(row), rowKey: String(row.id ?? 'none') })),
    // `labelOf` solo depende de `groupBy` y de `intl`
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadedRows, groupBy, intl],
  )
  const searching = search.trim() !== ''
  const rows = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase('es')
    const matching = needle === '' ? allRows : allRows.filter((row) => row.label.toLocaleLowerCase('es').includes(needle))
    return [...matching].sort((a, b) => compareRows(a, b, sort))
  }, [allRows, search, sort])
  // Con una búsqueda se ven todas las que coinciden: el tope es para recorrer la lista, no para ocultar resultados
  const shownRows = searching || showAll ? rows : rows.slice(0, TOP_ROWS)
  const hasMore = !searching && rows.length > TOP_ROWS
  const showSearch = allRows.length > TOP_ROWS

  const refreshing = groups.status === 'loading'
  const centered = { className: 'nv-urg-center', headerClassName: 'nv-urg-center' }

  const header = (id: string, hintId?: string): ReactNode => (
    <span className="nv-or-th">
      {intl.formatMessage({ id })}
      {hintId && <InfoTip text={intl.formatMessage({ id: hintId })} />}
    </span>
  )

  const nameBody = (row: GroupRow): ReactNode => (
    <span className="nv-or-name">
      <span>{row.label}</span>
      {isAgenda && row.archived && <Tag className="nv-tag-neutral" value={intl.formatMessage({ id: 'OperatingRoomIndicatorsArchivedTag' })} />}
    </span>
  )

  /** «Mar 08:00–14:00»: el día de la semana y el bloque. */
  const scheduleText = (row: GroupRow): string => {
    if (row.id === null || row.dayOfWeek === null || !row.fromTime || !row.toTime) return NO_VALUE
    return intl.formatMessage({ id: 'OperatingRoomIndicatorsBlockSchedule' }, {
      day: days[row.dayOfWeek - 1] ?? '', from: row.fromTime, to: row.toTime,
    })
  }

  const actionBody = (row: GroupRow): ReactNode => {
    // Sin dato no se puede filtrar (salvo «Sin agenda», que es el filtro «Fuera de agenda»)
    if (row.id === null && !isAgenda) return null
    return (
      // TODO(design-system): el ojo (ver detalle) abre acá la lista filtrada, no un detalle: no hay un ícono fijo para «ver en la lista».
      <Button
        type="button"
        text
        className="nv-icon nv-tip"
        icon="pi pi-eye"
        aria-label={intl.formatMessage({ id: 'OperatingRoomIndicatorsViewSurgeriesOf' }, { name: row.label })}
        data-pr-tooltip={intl.formatMessage({ id: 'OperatingRoomIndicatorsViewSurgeries' })}
        onClick={() => onDrill(groupBy, row)}
      />
    )
  }

  return (
    <div className="nv-or-group">
      <div className="nv-or-toolbar">
        <SelectButton
          value={groupBy}
          options={groupOptions}
          optionLabel="label"
          optionValue="value"
          onChange={(event) => { if (event.value) changeGroupBy(event.value as OperatingRoomGroupBy) }}
          aria-label={intl.formatMessage({ id: 'OperatingRoomIndicatorsGroupBy' })}
        />
        {showSearch && (
          <span className="p-input-icon-left">
            <i className="pi pi-search" aria-hidden="true" />
            <InputText
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={intl.formatMessage({ id: 'OperatingRoomIndicatorsGroupSearch' })}
              aria-label={intl.formatMessage({ id: 'OperatingRoomIndicatorsGroupSearch' })}
            />
          </span>
        )}
      </div>

      <div className="nv-or-group__body" aria-busy={refreshing}>
        {refreshing && allRows.length === 0 && (
          <span className="nv-loading" role="status">
            <ProgressSpinner strokeWidth="4" style={{ width: 16, height: 16 }} />
            <span>{intl.formatMessage({ id: 'OperatingRoomIndicatorsLoading' })}</span>
          </span>
        )}
        {groups.status === 'failed' && (
          <div className="nv-or-failed" role="alert">
            <p className="nv-note">
              <i className="pi pi-info-circle" aria-hidden="true" />
              {intl.formatMessage({ id: 'OperatingRoomIndicatorsGroupsError' })}
            </p>
            <Button type="button" link label={intl.formatMessage({ id: 'OperatingRoomIndicatorsRetry' })} onClick={groups.retry} />
          </div>
        )}
        {groups.status !== 'failed' && !(refreshing && allRows.length === 0) && (allRows.length === 0
          ? <p className="nv-urg-empty">{intl.formatMessage({ id: 'OperatingRoomIndicatorsEmpty' })}</p>
          : (
            <div className={`nv-load-table${refreshing ? ' nv-or-blurred' : ''}`}>
              <DataTable
                // Al cambiar de agrupamiento las columnas son otras
                key={groupBy}
                value={shownRows}
                dataKey="rowKey"
                lazy
                sortField={sort.field}
                sortOrder={sort.order}
                onSort={(event: DataTableSortEvent) => setSort({ field: event.sortField as SortField, order: event.sortOrder === 1 ? 1 : -1 })}
                emptyMessage={intl.formatMessage({ id: 'OperatingRoomIndicatorsGroupNoMatches' })}
              >
                <Column
                  field="name"
                  header={intl.formatMessage({ id: NAME_HEADER_ID[groupBy] })}
                  sortable
                  body={nameBody}
                  footer={totals ? intl.formatMessage({ id: 'OperatingRoomIndicatorsTotal' }) : undefined}
                />
                {isAgenda && (
                  <Column field="roomName" header={intl.formatMessage({ id: 'OperatingRoomIndicatorsColRoom' })} sortable body={(row: GroupRow) => row.roomName ?? NO_VALUE} />
                )}
                {isAgenda && (
                  <Column header={intl.formatMessage({ id: 'OperatingRoomIndicatorsColSchedule' })} body={scheduleText} />
                )}
                <Column
                  field="surgeries"
                  header={intl.formatMessage({ id: 'OperatingRoomIndicatorsKpiSurgeries' })}
                  sortable
                  {...centered}
                  footer={totals ? formatCount(intl, totals.surgeries) : undefined}
                  body={(row: GroupRow) => formatCount(intl, row.surgeries)}
                />
                <Column
                  field="sharePct"
                  header={intl.formatMessage({ id: 'OperatingRoomIndicatorsColShare' })}
                  sortable
                  {...centered}
                  footer={totals ? formatPercent(intl, totals.surgeries > 0 ? 100 : null) : undefined}
                  body={(row: GroupRow) => formatPercent(intl, row.sharePct)}
                />
                <Column
                  field="urgent"
                  header={header('OperatingRoomIndicatorsKpiUrgent', 'OperatingRoomIndicatorsKpiUrgentHint')}
                  sortable
                  {...centered}
                  footer={totals ? countWithShare(intl, totals.urgent, totals.urgentPct) : undefined}
                  body={(row: GroupRow) => countWithShare(intl, row.urgent, row.urgentPct)}
                />
                {!isAgenda && (
                  <Column
                    field="scheduledPct"
                    header={header('OperatingRoomIndicatorsKpiScheduled', 'OperatingRoomIndicatorsKpiScheduledHint')}
                    sortable
                    {...centered}
                    footer={totals ? countWithShare(intl, totals.scheduled, totals.scheduledPct) : undefined}
                    body={(row: GroupRow) => countWithShare(intl, row.scheduled, row.scheduledPct)}
                  />
                )}
                <Column
                  field="hoursUsed"
                  header={header('OperatingRoomIndicatorsColHours', 'OperatingRoomIndicatorsKpiHoursHint')}
                  sortable
                  {...centered}
                  footer={totals ? formatHoursPlain(intl, totals.hoursUsed) : undefined}
                  body={(row: GroupRow) => formatHoursPlain(intl, row.hoursUsed)}
                />
                <Column
                  field="medianDurationMinutes"
                  header={intl.formatMessage({ id: 'OperatingRoomIndicatorsColMedian' })}
                  sortable
                  {...centered}
                  footer={totals ? formatDuration(intl, totals.medianDurationMinutes) : undefined}
                  body={(row: GroupRow) => formatDuration(intl, row.medianDurationMinutes)}
                />
                {isAgenda && (
                  <Column
                    field="capacityHours"
                    header={header('OperatingRoomIndicatorsColCapacity', 'OperatingRoomIndicatorsColCapacityAgendaHint')}
                    sortable
                    {...centered}
                    footer={totals ? formatHoursPlain(intl, totals.capacityHours) : undefined}
                    body={(row: GroupRow) => formatHoursPlain(intl, row.capacityHours)}
                  />
                )}
                {isAgenda && (
                  <Column
                    field="insideBlockHours"
                    header={intl.formatMessage({ id: 'OperatingRoomIndicatorsColInside' })}
                    sortable
                    {...centered}
                    footer={totals ? formatHoursPlain(intl, totals.insideBlockHours) : undefined}
                    body={(row: GroupRow) => formatHoursPlain(intl, row.insideBlockHours)}
                  />
                )}
                {isAgenda && (
                  <Column
                    field="occupancyPct"
                    header={header('OperatingRoomIndicatorsKpiOccupancy', 'OperatingRoomIndicatorsKpiOccupancyHint')}
                    sortable
                    {...centered}
                    footer={totals ? formatPercent(intl, totals.occupancyPct) : undefined}
                    body={(row: GroupRow) => formatPercent(intl, row.occupancyPct)}
                  />
                )}
                <Column
                  header={<span className="nv-sr-only">{intl.formatMessage({ id: 'OperatingRoomIndicatorsViewSurgeries' })}</span>}
                  className="nv-col-actions"
                  headerClassName="nv-col-actions"
                  body={actionBody}
                />
              </DataTable>
            </div>
          ))}
      </div>

      {hasMore && (
        <div className="nv-or-more">
          <Button
            type="button"
            link
            label={intl.formatMessage(
              { id: showAll ? 'OperatingRoomIndicatorsShowLess' : 'OperatingRoomIndicatorsShowAll' },
              { count: rows.length, countText: formatCount(intl, rows.length), top: TOP_ROWS },
            )}
            aria-expanded={showAll}
            onClick={() => setShowAll((value) => !value)}
          />
        </div>
      )}
    </div>
  )
}
